import { ErrorBoundary } from "~/lib/Bugsnag";
import { ErrorView } from "~/components/shared/ErrorView";
import { useNavigate } from "react-router-dom";
import { StepTree } from "~/components/build/Show/pages/steps/list/components/StepTree";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { useTreeNodes } from "~/components/build/Show/pages/steps/list/components/useTreeNodes";
import {
  DisplayMode,
  useFilterStore,
  type FilterByOption,
} from "~/components/build/Show/lib/useFilterStore";
import { useQuery } from "@tanstack/react-query";
import { allStepsQueryOptions } from "~/components/build/Show/lib/queries/steps";
import { useRef } from "react";
import { HiddenStepsSummary } from "~/components/build/Show/pages/steps/list/components/HiddenStepsSummary";
import { STEP_ITEM_HEIGHT } from "~/components/build/Show/pages/steps/list/components/constants";

export function ListView() {
  const { build } = useBuild();
  const displayMode = useFilterStore((state) => state.displayMode || DisplayMode.State);
  const filters: FilterByOption[] = useFilterStore((state) => state.filterBy);
  const { isLoading, nodes } = useTreeNodes({ displayMode, filters });

  // Fetch all steps for pipeline mode (needed for search/table features).
  useQuery(
    allStepsQueryOptions(build, {
      enabled: displayMode === DisplayMode.Pipeline,
    }),
  );

  const scrollRef = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={scrollRef}
      className="no-scrollbar absolute inset-0 flex flex-col overflow-y-auto"
      style={{ contain: "strict" }}
      data-testid="list-view"
    >
      <div className="relative">
        <div className="sticky top-0 z-[25] h-2 w-full bg-white" />
        <div className="@container/list flex flex-col px-2">
          {isLoading ? (
            <ul className="flex flex-col gap-2">
              {Array.from({ length: Math.min(build.statistics?.stepsCount || 0, 10) }).map(
                (_item, index) => (
                  <li
                    key={index}
                    style={{ height: STEP_ITEM_HEIGHT }}
                    className="not-inverted w-full animate-pulse rounded-sm bg-gray-200 dark:bg-white/15"
                  />
                ),
              )}
            </ul>
          ) : (
            <StepTree displayMode={displayMode} nodes={nodes} scrollRef={scrollRef} offsetTop={8} />
          )}
          <HiddenStepsSummary filters={filters} />
        </div>
        <div className="pointer-events-none sticky bottom-0 z-[25] h-9 w-full rounded-b-md bg-gradient-to-t from-white/90 to-transparent" />
      </div>
    </div>
  );
}

export default function ListPage() {
  const navigate = useNavigate();

  return (
    <ErrorBoundary
      FallbackComponent={() => <ErrorView className="px-4 py-2" clearError={() => navigate(0)} />}
    >
      <div
        className="relative min-w-0 flex-auto rounded-b-md bg-white contain-layout"
        data-testid="list"
      >
        <ListView />
      </div>
    </ErrorBoundary>
  );
}
